'use client';

import dynamic from 'next/dynamic';
import { memo } from 'react';
import { useIsClient } from 'usehooks-ts';

import {
  BANNER_CONFIG,
  BannerPlacement,
  LotriverConfig
} from '@/constants/banners';
import { useBannerLoad } from '@/providers/BannerLoadProvider';

const Banner = dynamic(
  () => import('@/components/Banner/Banner').then(mod => mod.Banner),
  { ssr: false }
);

export interface BannerSlotProps {
  placement: BannerPlacement;
  overrides?: Partial<LotriverConfig>;
}

export const BannerSlot = memo<BannerSlotProps>(({ placement, overrides }) => {
  // Breakpoint-derived `bn` overrides resolve from their SSR default to the real
  // value on mount. Banner's ad request re-fires on every `bn` change, so
  // rendering before the breakpoint is known would issue a wrong/duplicate ad
  // request on client navigations. Wait for the client to avoid that transient.
  const isClient = useIsClient();
  const { canLoadPageTopBanner } = useBannerLoad();
  const config = BANNER_CONFIG[placement];

  if (!config) return null;

  const { containerId, containerClassName, sid, bn, arCn, shouldOffsetHeader } =
    { ...config, ...overrides };

  if (
    !containerId ||
    (shouldOffsetHeader && !canLoadPageTopBanner) ||
    !isClient
  )
    return null;

  return (
    <Banner
      containerId={containerId}
      containerClassName={containerClassName}
      sid={sid}
      bn={bn}
      arCn={arCn}
      shouldOffsetHeader={shouldOffsetHeader}
    />
  );
});

BannerSlot.displayName = 'BannerSlot';
